import React from 'react'
import { IEventScheduleObject, ITemplateConfig } from 'tui-calendar'
import Calendar from '@toast-ui/react-calendar'
import format from '../../format'

import { getTuiScheduleProps } from '../../Models/workshops'

import 'tui-calendar/dist/tui-calendar.css'
import 'tui-date-picker/dist/tui-date-picker.css';
import 'tui-time-picker/dist/tui-time-picker.css';
import { WorkshopEventsViewProps } from '../WorkshopEventsView';
import { assertUnreachable } from '../../utility';
import { Language, LocalizedText } from '../../language'
import { EventExpanded } from '../EventList/EventExpanded'
import { Modal } from '../../Components/Modal'
import classNames from 'classnames'

import './EventCalendar.scss'
import { EventTargetWithClassName } from '../../Models/system'

export type CalendarNavigateAction = 'lastMonth' | 'today' | 'nextMonth'

function isCurrentMonth(calendarRef: React.RefObject<Calendar>) {
	const calendarInstance = calendarRef.current?.getInstance()

	const calendarDate = calendarInstance?.getDate().toDate()
	const now = new Date()

	return (now.getFullYear() == calendarDate?.getFullYear()) && (now.getMonth() == calendarDate.getMonth())
}

export interface EventCalendarProps extends WorkshopEventsViewProps {
	// filteredEvents: Array<WorkshopEvent>
	language: Language
	selectWorkshopEventId: (workshopEventId?: number) => void
	localizedText: LocalizedText
}

// can't seem to stop propagation in first click handler... possibly because of the distinction between native & synthetic events
// we now handle this in the base component by checking the event.path for the right class... it's a bit of a hack
export const clickWasOnCalendarEvent = (event: MouseEvent) => {
	const pathArray = event.composedPath() as Array<EventTargetWithClassName>
	const wasOnCalendarEvent = pathArray && pathArray.some(e => e.className?.endsWith('tui-full-calendar-weekday-schedule tui-full-calendar-weekday-schedule-time'))
	return wasOnCalendarEvent
}

export function EventCalendar(props: EventCalendarProps) {
	const calendarRef = React.useRef<Calendar>(null)
	const [displayedDate, setDisplayedDate] = React.useState<Date>()

	// const tuiScheduleProps = getTuiScheduleProps(props.workshop, props.filteredEvents, props.language)
	const tuiScheduleProps = getTuiScheduleProps(props.workshop, props.filterState.ranges.filteredWorkshopEvents, props.language)

	// TODO: change this to use the data-action
	const getCalendarNavHandler = (action: CalendarNavigateAction) => () => {
		const calendarInstance = calendarRef.current?.getInstance();

		switch(action) {
			case 'lastMonth': calendarInstance?.prev(); break;
			case 'nextMonth': calendarInstance?.next(); break;
			case 'today': calendarInstance?.today(); break;
			default: assertUnreachable(action)
		}

		setDisplayedDate(calendarRef.current?.getInstance().getDate().toDate())
	}

	function handleScheduleClick(event: IEventScheduleObject) {
		const workshopEventId = event.schedule.raw!.workshopEventId as number

		// this doesn't work either... filteredEvents appears to be cached somehow from prior renders?
		// const workshopEvent = props.filteredEvents.find(e => e.EventId == workshopEventId)!

		// can't seem to stop propagation in first click handler... possibly because of the distinction between native & synthetic events
		// we now handle this in the base component by checking the event.target.class... it's a bit of a hack
		// event.event.stopPropagation()

		// props.getWorkshopEventClickHandler(workshopEvent)(event.event as any)
		props.selectWorkshopEventId(workshopEventId)
	}

	// const selectedDate = calendarRef.current?.getInstance().getDate().toDate() || new Date()
	const monthNameAndYear = format.monthNameAndYear(displayedDate || new Date())

	let expandedSelectedEvent: React.ReactElement | null = null
	if (props.selectedWorkshopEventId) {
		// const selectedWorkshopEvent = props.filteredEvents.find(we => we.EventId == props.selectedWorkshopEventId)!
		const selectedWorkshopEvent = props.filterState.ranges.filteredWorkshopEvents.find(we => we.EventId == props.selectedWorkshopEventId)!
		expandedSelectedEvent = 
			<Modal contentStyle={{maxWidth: '1024px', overflowY: 'auto'}}>
				<EventExpanded 
					onClick={props.getWorkshopEventClickHandler(selectedWorkshopEvent)} 
					onCloseClick={() => props.selectWorkshopEventId(undefined)}
					onDismissRegistrationSuccessfulMessage={() => { }} 
					onRegisterClick={props.onRegisterClick} 
					event={selectedWorkshopEvent} 
				/>
			</Modal>
	}

	const calendarTemplate: ITemplateConfig = {
		monthDayname: (model) => format.dayName(model.day), 
		time: (schedule) => `<span class='${classNames('event-title', schedule.raw?.isParticipating && 'is-participating')}'>${schedule.title}</span>`
	}

	return (
		<div className='event-calendar'>
			<div className='is-clearfix'>
				<button className='button' data-action='move-prev' onClick={getCalendarNavHandler("lastMonth")} disabled={isCurrentMonth(calendarRef)}>{"<"}</button>
				<button className='button' data-action='move-today' onClick={getCalendarNavHandler("today")}>{props.localizedText.Today}</button>
				<button className='button' data-action='move-next' onClick={getCalendarNavHandler("nextMonth")}>{">"}</button>
				<span className='is-size-3 is-pulled-right'>{monthNameAndYear}</span>
			</div>
			<Calendar ref={calendarRef} height='600px' usageStatistics={false} view='month' template={calendarTemplate} isReadOnly={true} onClickSchedule={handleScheduleClick} {...tuiScheduleProps}  />
			{expandedSelectedEvent}
		</div>
	)
}

export default EventCalendar